import { format } from "date-fns";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { DietMenuModalBody } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-body";
import { DietMenuModalHeader } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-header";
import { DietMenuModalTopBar } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-top-bar";
import type { OmnibusListingPrices } from "@/features/diets/helpers/omnibus";
import { useDietMenuPreview } from "@/features/diets/hooks/use-diet-menu-preview";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { DietPricingComplex } from "@/features/diets/types/diet-pricing-complex.type";
import { useDietDates } from "@/lib/hooks/use-diet-dates";

type DietMenuModalContentProps = {
  data: DietCatalogDetail;
  configuration?: Record<string, string | number | string[]>;
  showCta?: boolean;
  dietId: string;
  availableCalorifics?: Array<{ id: string; calories: number }>;
  selectedCalorific?: {
    id: string;
    calories: number;
  };
  type?: "multidiet" | "default";
  numberOfDays?: number;
  price?: number;
  priceComplex?: DietPricingComplex;
  // Omnibus prices of the listing item or configured quote (MPL-1835).
  omnibusPrices?: OmnibusListingPrices;
  onInitialMenuLoaded?: () => void;
};

export const DietMenuModalContent = ({
  data,
  configuration,
  showCta,
  dietId,
  availableCalorifics,
  selectedCalorific,
  type,
  numberOfDays,
  price,
  priceComplex,
  omnibusPrices,
  onInitialMenuLoaded,
}: DietMenuModalContentProps) => {
  const locale = useLocale();
  const [activeMealIngredients, setActiveMealIngredients] = useState<
    string | null
  >(null);
  const [activeMealIngredientsId, setActiveMealIngredientsId] = useState<
    number | null
  >(null);

  const [activeMealId, setActiveMealId] = useState<string | null>(null);
  const [activeMealName, setActiveMealName] = useState<string | null>(null);
  const [calories, setCalories] = useState(selectedCalorific);
  const dietDates = useDietDates({
    diet: data,
    saturdays: true,
    sundays: true,
    locale,
  });

  const [currentDate, setCurrentDate] = useState(
    new Date(dietDates.nextPossibleDay(new Date())),
  );

  const [isScrolled, setIsScrolled] = useState(false);

  const { data: menu, isLoading: isMenuLoading } = useDietMenuPreview({
    dietId,
    date: format(currentDate, "yyyy-MM-dd"),
    caloricityId: calories?.id ?? selectedCalorific?.id ?? "",
  });

  useEffect(() => {
    if (isMenuLoading || !menu) {
      return;
    }

    onInitialMenuLoaded?.();
  }, [isMenuLoading, menu, onInitialMenuLoaded]);

  return (
    <>
      <DietMenuModalTopBar
        activeMealIngredients={activeMealIngredients}
        currentDate={currentDate}
        isScrolled={isScrolled}
        setActiveMealIngredients={setActiveMealIngredients}
      />
      <div
        className="overflow-y-scroll md:overflow-hidden"
        onScroll={(e) => {
          const target = e.currentTarget;
          setIsScrolled(target.scrollTop > 200);
        }}
      >
        <DietMenuModalHeader
          activeMealIngredients={activeMealIngredients}
          activeMealName={activeMealName}
          configuration={configuration}
          currentDate={currentDate}
          data={data}
          numberOfDays={numberOfDays}
          price={price}
          omnibusPrices={omnibusPrices}
          priceComplex={priceComplex}
          selectedCalories={calories?.calories.toString()}
          setCurrentDate={setCurrentDate}
          showCta={showCta}
          type={type}
        />
        <DietMenuModalBody
          activeMealId={activeMealId}
          activeMealIngredients={activeMealIngredients}
          activeMealIngredientsId={activeMealIngredientsId}
          availableCalorifics={availableCalorifics}
          calories={calories ?? { id: "", calories: 0 }}
          data={data}
          dietId={dietId}
          isMenuLoading={isMenuLoading}
          menu={menu}
          setActiveMealId={setActiveMealId}
          setActiveMealIngredients={setActiveMealIngredients}
          setActiveMealIngredientsId={setActiveMealIngredientsId}
          setActiveMealName={setActiveMealName}
          setCalories={setCalories}
          type={type}
        />
      </div>
    </>
  );
};
